import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Trash2, Save, RefreshCw, Headphones, Lock, Download } from "lucide-react";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const VoiceRecorder = ({ userId, bookId, slot, saved, onSaved, accent = "#385E3A" }) => {
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(!!saved);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Le micro n'est pas disponible sur ce navigateur.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mrRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch {
      toast.error("Autorisez l'accès au microphone pour enregistrer.");
    }
  };

  const stop = () => {
    if (mrRef.current && mrRef.current.state !== "inactive") mrRef.current.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const reset = () => { setBlob(null); setBlobUrl(null); setElapsed(0); };

  const save = async () => {
    if (!blob) return;
    setBusy(true);
    try {
      const b64 = await new Promise((res) => {
        const r = new FileReader();
        r.onloadend = () => res(r.result.split(",")[1]);
        r.readAsDataURL(blob);
      });
      await api.post("/recordings", {
        user_id: userId, book_id: bookId, slot,
        mime: blob.type || "audio/webm", audio: b64,
      });
      setDone(true);
      reset();
      toast.success("Message conservé");
      onSaved && onSaved();
    } catch {
      toast.error("Échec de la sauvegarde du message.");
    } finally {
      setBusy(false);
    }
  };

  const removeSaved = async () => {
    await api.delete(`/recordings/${userId}/${bookId}/${slot}`);
    setDone(false);
    onSaved && onSaved();
  };

  if (done) {
    return (
      <div data-testid={`rec-saved-${slot}`} className="rounded-xl border border-[#C9A24B]/50 bg-[#F8F5EE] p-4 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 font-ui text-sm text-[#1F2A24]">
          <Lock size={16} className="text-[#C9A24B]" /> Message conservé
        </span>
        <button data-testid={`rec-refaire-${slot}`} onClick={removeSaved} className="font-sans text-xs text-[#1F2A24]/50 hover:text-[#A63B36] inline-flex items-center gap-1">
          <RefreshCw size={13} /> Refaire
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`rec-${slot}`} className="rounded-xl border border-[#E8DCC4] bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="font-ui text-sm text-[#1F2A24]/60">Durée recommandée : 1 à 3 min</span>
        <span className="font-serif text-lg" style={{ color: accent }}>{fmt(elapsed)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {!recording && !blob && (
          <button data-testid={`rec-start-${slot}`} onClick={start} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-ui text-sm text-white transition-opacity hover:opacity-90" style={{ backgroundColor: accent }}>
            <Mic size={16} /> ENREGISTRER
          </button>
        )}
        {recording && (
          <button data-testid={`rec-stop-${slot}`} onClick={stop} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-ui text-sm text-white bg-[#A63B36] animate-pulse">
            <Square size={15} /> ARRÊTER
          </button>
        )}
        {blob && !recording && (
          <>
            <audio data-testid={`rec-audio-${slot}`} src={blobUrl} controls className="h-9 max-w-[220px]" />
            <button data-testid={`rec-again-${slot}`} onClick={reset} className="inline-flex items-center gap-1.5 rounded-full border border-[#E8DCC4] px-4 py-2.5 font-ui text-sm text-[#1F2A24]">
              <RefreshCw size={15} /> Recommencer
            </button>
            <button data-testid={`rec-delete-${slot}`} onClick={reset} className="inline-flex items-center gap-1.5 rounded-full border border-[#E8DCC4] px-4 py-2.5 font-ui text-sm text-[#A63B36]">
              <Trash2 size={15} /> Supprimer
            </button>
            <button data-testid={`rec-save-${slot}`} disabled={busy} onClick={save} className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 font-ui text-sm text-white bg-[#385E3A] hover:bg-[#2c4a2e] disabled:opacity-50">
              <Save size={15} /> SAUVEGARDER
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const AudioPlayer = ({ userId, bookId, slot, label = "ÉCOUTER MON MESSAGE", accent = "#C9A24B" }) => {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/recordings/${userId}/${bookId}/${slot}`);
      setSrc(`data:${r.data.mime};base64,${r.data.audio}`);
    } catch (e) {
      toast.error(e && e.response && e.response.status === 403 ? "Message verrouillé jusqu'au Jour 21" : "Message introuvable");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`player-${slot}`}>
      {!src ? (
        <button data-testid={`player-load-${slot}`} disabled={busy} onClick={load} className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-ui text-sm text-[#1F2A24] transition-opacity hover:opacity-90" style={{ backgroundColor: accent }}>
          <Headphones size={17} /> {label}
        </button>
      ) : (
        <div className="flex flex-col gap-2">
          <audio data-testid={`player-audio-${slot}`} src={src} controls autoPlay className="w-full max-w-md" />
          <a
            data-testid={`player-download-${slot}`}
            href={src}
            download={`tao-des-4c-${slot}.webm`}
            className="inline-flex items-center gap-1.5 font-sans text-xs text-[#1F2A24]/70 hover:text-[#385E3A]"
          >
            <Download size={13} /> Télécharger
          </a>
        </div>
      )}
    </div>
  );
};

export const VoiceJourney = () => (
  <div data-testid="voice-journey" className="rounded-2xl border border-[#C9A24B]/40 bg-[#1F2A24] text-[#F6F2E9] p-6 md:p-8 text-center">
    <h3 className="font-serif text-2xl text-[#C9A24B]">J'ai rendez-vous avec mon futur</h3>
    <div className="mt-6 space-y-2 font-sans text-sm">
      <p className="font-ui tracking-[0.2em] text-[#F6F2E9]/70">JOUR 1</p>
      <p>🎙️ Moi du futur → Moi d'aujourd'hui</p>
      <p className="text-[#C9A24B] py-1">↓ 21 jours ↓</p>
      <p className="font-ui tracking-[0.2em] text-[#F6F2E9]/70">JOUR 21</p>
      <p>🎙️ Moi d'aujourd'hui → Moi du passé</p>
    </div>
  </div>
);
